import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import crypto from "crypto";
import path from "path";

dayjs.extend(utc);
dayjs.extend(timezone);

const tz = "Asia/Bangkok";

export const getSafe = (fn: any, d: any = undefined) => {
  try {
    return fn();
  } catch (error) {
    return d;
  }
};

export const asyncSafe = async (fn: any, d: any = undefined) => {
  try {
    return await fn();
  } catch (error) {
    return d;
  }
};

export const getImage = (src: string) => {
  return src ? src : "/images/placeholder.png";
};

export const getMenuUrl = (
  lang: string,
  path: string,
  isBasePath: boolean = false
) => {
  let basePath = process.env.NEXT_PUBLIC_PATH_URL || "";
  const appFolder = process.env.NEXT_PUBLIC_APP_FOLDER || "";
  let subPath = basePath;
  const isCurrentPath = getSafe(() => path.split("/")[1] === appFolder, true);
  const isCurrentDomain = basePath.includes(appFolder);

  if (isCurrentPath && isCurrentDomain) {
    if (isBasePath && basePath !== "") {
      return `/${basePath}/${lang}/${
        path.startsWith("/") ? path.slice(1) : path
      }`;
    } else {
      return `/${lang}/${path.startsWith("/") ? path.slice(1) : path}`;
    }
  } else {
    subPath = getSafe(
      () => subPath.replace(appFolder, `${path.split("/")[1]}`),
      subPath
    );

    return `${process.env.NEXT_PUBLIC_BASEURL}/${subPath}/${lang}/${
      path.startsWith("/") ? path.slice(1) : path
    }`;
  }
};

export const getUrl = (
  lang: string,
  path: string,
  isBasePath: boolean = false
) => {
  const basePath = process.env.NEXT_PUBLIC_PATH_URL || "";
  if (isBasePath && basePath !== "") {
    return `/${basePath}/${lang}/${
      path.startsWith("/") ? path.slice(1) : path
    }`;
  } else {
    return `/${lang}/${path.startsWith("/") ? path.slice(1) : path}`;
  }
  /*return `${
    basePath !== ""
      ? `/${basePath}/${lang}/${path.startsWith("/") ? path.slice(1) : path}`
      : `/${lang}/${path.startsWith("/") ? path.slice(1) : path}`
  }`;*/
};

export const getUrlWithDomain = (lang: string, path: string) => {
  const domain = process.env.NEXT_PUBLIC_DOMAIN || "";
  return `${domain}${`/${lang}/${
    path.startsWith("/") ? path.slice(1) : path
  }`}`;
};

export const getBaseURL = (lang: string) => {
  const basePath = process.env.NEXT_PUBLIC_PATH_URL || "";
  /*return `${
    basePath !== ""
      ? `/${process.env.NEXT_PUBLIC_PATH_URL}/${lang}`
      : `/${lang}`
  }`;*/
  return `/${lang}`;
};

export const sha256Hash = async (string: any) => {
  /*const utf8 = new TextEncoder().encode(string);
  const hashBuffer = await crypto.subtle.digest("SHA-256", utf8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((bytes) => bytes.toString(16).padStart(2, "0"))
    .join("");*/
  const hashHex = crypto.createHash("sha256").update(string).digest("hex");
  return hashHex;
};

export const getAppCookie = (cookieName: string) => {
  let cookie: any = {};
  document.cookie.split(";").forEach((el) => {
    let [key, value] = el.split("=");
    cookie[key.trim()] = value;
  });
  return cookie[cookieName];
};

export const getQueryParams = (paramName: string) => {
  try {
    const queryString = window.location.search;
    const params = new URLSearchParams(queryString);
    const paramValue = params.get(paramName);
    return paramValue;
  } catch (error) {
    console.error("Error retrieving query parameter:", error);
    return undefined;
  }
};

export const checkPermission = (
  url: string,
  roleUrlList: Array<any>,
  lang: string = "th"
) => {
  if (!roleUrlList.includes(url)) {
    window.location.href = getUrl(lang, "404");
  }
};

export const getUserInstitutionList = (userInstitution: any, lang: string) => {
  return userInstitution.map((item: any) => {
    return {
      ...item,
      value: item.institutionId,
      label:
        item[`institutionName${lang.toUpperCase()}`] ||
        item["institutionName"][lang],
    };
  });
};

export const getTableColumn = (columns: Array<any>) => {
  try {
    return Object.keys(columns[0]);
  } catch (error) {
    return [];
  }
};

export const formatDateTime = (
  date: string | Date = dayjs().format("YYYY-MM-DD HH:mm:ss"),
  dayjsFormat: string,
  isTimezone: boolean = true
) => {
  const parsedDate = typeof date === "string" ? dayjs(date) : date;

  if (isTimezone) {
    return dayjs(parsedDate)
      .utc()
      .tz(tz)
      .format(dayjsFormat || "YYYY-MM-DD HH:mm:ss");
  } else {
    return dayjs(parsedDate)
      .utc()
      .format(dayjsFormat || "YYYY-MM-DD HH:mm:ss");
  }

  //return parsedDate.tz(tz).format(dayjsFormat || "YYYY-MM-DD");
};

export const fileToBase64 = (file: any) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const base64toBlob = (
  base64Data: string,
  contentType: string = "application/pdf"
) => {
  contentType = contentType || "";
  const byteCharacters = atob(base64Data);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

const isValidBase64 = (str: string) => {
  try {
    return btoa(atob(str)) == str;
  } catch (err) {
    return false;
  }
};

export const base64ToFile = (
  base64String: string,
  fileName: string = "file",
  contentType: string = "application/pdf"
) => {
  if (isValidBase64(base64String)) {
    const blob = base64toBlob(base64String, contentType);
    const url = URL.createObjectURL(blob);

    return { url, blob, fileName };
  } else {
    console.log("Invalid base64 string", base64String);
    return {
      url: undefined,
      blob: new Blob(),
      fileName,
      error: "Invalid base64 string",
    };
  }
};

export const getPublicAssetPath = (pathURI: string) => {
  try {
    const publicPath = process.env.NEXT_PUBLIC_PATH_URL;

    return path.join(
      `${publicPath !== "" ? `/${publicPath || ""}${pathURI}` : `${pathURI}`}`
    );
  } catch (e) {
    return pathURI;
  }
};

export const getSessionInstiutionList = (userInstitution: any) => {
  return getSafe(
    () =>
      JSON.stringify({
        institutionList: [
          ...[userInstitution.ownInstitution],
          ...userInstitution.authorizedInstitution,
        ].map((item: any) => item["_id"]),
      }),
    JSON.stringify({ institutionList: [] })
  );
};

export const getSessionInstiution = (userInstitution: any, roles: any) => {
  const isIssuer = roles.includes("issuer") || roles.includes("temp");
  if (isIssuer) {
    return getSafe(
      () => [
        ...[userInstitution.ownInstitution],
        ...userInstitution.authorizedInstitution,
      ],
      []
    );
  } else {
    return getSafe(() => [...userInstitution.authorizedInstitution], []);
  }
};
